import { useEffect, useMemo, useState } from 'react';
import { MODELS, QUANT_LABEL, findModel } from '../catalog.js';
import { estimateFit, recommendQuantForMachine, recommendRuntimeForMachine } from '../fit.js';
import { type RuntimeProbe, probeAllRuntimes } from '../ollama.js';
import { type DetectedMachine, closestPreset, detectMachine, toMachineProfile } from '../scan.js';
import type { MachineProfile, Quantization, RuntimeId } from '../types.js';
import { Bar, ConfidenceDot, StatRow, TierBadge, VerdictPill } from '../ui.js';

interface ScanTabProps {
  machine: MachineProfile;
  mode: 'beginner' | 'pro';
  onApplyDetected: (machine: MachineProfile | null) => void;
  scannedMachine: MachineProfile | null;
}

export function ScanTab({ machine, mode, onApplyDetected, scannedMachine }: ScanTabProps) {
  const defaultRuntime = recommendRuntimeForMachine(machine);
  const [selectedModelId, setSelectedModelId] = useState<string>('qwen-2.5-coder-14b');
  const [runtime, setRuntime] = useState<RuntimeId>(defaultRuntime);
  const model = findModel(selectedModelId) ?? MODELS[0];

  const recommendedQuant = useMemo(() => recommendQuantForMachine(machine, model), [machine, model]);
  const [quant, setQuant] = useState<Quantization>(recommendedQuant);

  const [detected, setDetected] = useState<DetectedMachine | null>(null);
  const [probes, setProbes] = useState<RuntimeProbe[] | null>(null);
  const [probing, setProbing] = useState(false);
  const [storageGb, setStorageGb] = useState<number>(200);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setDetected(detectMachine());
  }, []);

  async function handleProbeRuntimes() {
    setProbing(true);
    try {
      const result = await probeAllRuntimes();
      setProbes(result);
    } finally {
      setProbing(false);
    }
  }

  function handleApplyDetected() {
    if (!detected) return;
    const installed: RuntimeId[] = [];
    for (const p of probes ?? []) if (p.reachable) installed.push(p.runtime);
    if (installed.length === 0) installed.push('ollama');
    const profile = toMachineProfile(detected, installed);
    profile.storageFreeGb = storageGb;
    onApplyDetected(profile);
  }

  function handleApplyClosestPreset() {
    if (!detected) return;
    const preset = closestPreset(detected);
    if (preset) onApplyDetected(preset);
  }

  const activeQuant = model.quantizations.includes(quant) ? quant : recommendedQuant;
  const fit = useMemo(
    () => estimateFit(machine, model, activeQuant, runtime),
    [machine, model, activeQuant, runtime],
  );
  const allQuantFits = model.quantizations.map((q) => ({
    q,
    fit: estimateFit(machine, model, q, runtime),
  }));

  return (
    <div className="lab-scan-grid">
      <section className="lab-scan-detect">
        <header>
          <h4>Detect this machine</h4>
          <p className="lab-scan-help">
            We use the browser's hints (RAM, GPU renderer, user agent) to estimate your machine. Apply the
            detected profile to drive every fit estimate from your real hardware.
          </p>
        </header>
        {detected ? (
          <div className="lab-detect-block">
            <div className="lab-detect-row">
              <span>OS</span>
              <strong>{detected.os}</strong>
            </div>
            <div className="lab-detect-row">
              <span>Accelerator</span>
              <strong>{detected.accel}</strong>
            </div>
            <div className="lab-detect-row">
              <span>CPU / Chip</span>
              <strong>{detected.cpuLabel}</strong>
            </div>
            <div className="lab-detect-row">
              <span>RAM</span>
              <strong>{detected.ramGb} GB</strong>
            </div>
            <div className="lab-detect-row">
              <span>Usable for inference</span>
              <strong>{detected.vramGb} GB</strong>
            </div>
            <label className="lab-detect-row lab-detect-input">
              <span>Storage headroom</span>
              <input
                type="number"
                min={0}
                className="lab-input"
                value={storageGb}
                onChange={(e) => setStorageGb(Math.max(0, Number(e.target.value) || 0))}
              />
              <span className="lab-detect-unit">GB free</span>
            </label>
            <div className="lab-detect-row">
              <span>Confidence</span>
              <strong className={`lab-confidence-${detected.confidence}`}>
                <ConfidenceDot confidence={detected.confidence} /> {detected.confidence}
              </strong>
            </div>
            <div className="lab-detect-actions">
              <button type="button" className="primary-button" onClick={handleApplyDetected}>
                Use detected profile
              </button>
              {closestPreset(detected) && (
                <button type="button" className="ghost-button" onClick={handleApplyClosestPreset}>
                  Use closest preset
                </button>
              )}
              {scannedMachine && (
                <button type="button" className="ghost-button" onClick={() => onApplyDetected(null)}>
                  Reset to picker
                </button>
              )}
            </div>
            {detected.notes.length > 0 && (
              <ul className="lab-detect-notes">
                {detected.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <p className="lab-empty">Browser detection runs in the browser only.</p>
        )}

        <div className="lab-probe-block">
          <header>
            <h4>Detect installed runtimes</h4>
            <button type="button" className="ghost-button" onClick={handleProbeRuntimes} disabled={probing}>
              {probing ? 'Probing…' : 'Probe Ollama + LM Studio'}
            </button>
          </header>
          {probes && probes.length > 0 ? (
            <ul className="lab-probe-list">
              {probes.map((p) => (
                <li key={p.runtime} className={p.reachable ? 'lab-probe-ok' : 'lab-probe-fail'}>
                  <strong>{p.runtime}</strong>
                  <span>{p.reachable ? `OK · ${p.installedModels.length} models` : p.error}</span>
                  {p.reachable && p.installedModels.length > 0 && (
                    <details>
                      <summary>installed</summary>
                      <ul>
                        {p.installedModels.slice(0, 12).map((m) => (
                          <li key={m.name}>
                            {m.name}
                            {m.sizeGb ? ` · ${m.sizeGb} GB` : ''}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="lab-empty">
              Probes reach <code>127.0.0.1:11434</code> (Ollama) and <code>127.0.0.1:1234</code> (LM Studio).
              Set CORS or run the deck locally for the probe to succeed.
            </p>
          )}
        </div>
      </section>

      <aside className="lab-scan-picker">
        <h4>Check a specific model</h4>
        <label>
          <span>Model</span>
          <select
            className="lab-select"
            value={selectedModelId}
            onChange={(e) => {
              const next = e.target.value;
              setSelectedModelId(next);
              const m = findModel(next);
              if (m) setQuant(recommendQuantForMachine(machine, m));
            }}
          >
            {MODELS.map((m) => (
              <option key={m.id} value={m.id}>
                {m.displayName} — {m.paramsB}B · {m.codingTier}-tier
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Quantization</span>
          <select
            className="lab-select"
            value={activeQuant}
            onChange={(e) => setQuant(e.target.value as Quantization)}
          >
            {model.quantizations.map((q) => (
              <option key={q} value={q}>
                {QUANT_LABEL[q]} {q === recommendedQuant ? '· recommended' : ''}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Runtime</span>
          <select
            className="lab-select"
            value={runtime}
            onChange={(e) => setRuntime(e.target.value as RuntimeId)}
          >
            <option value="ollama">Ollama</option>
            <option value="lm-studio">LM Studio</option>
            <option value="llama-cpp">llama.cpp</option>
            <option value="mlx">MLX</option>
            <option value="vllm">vLLM</option>
          </select>
        </label>
      </aside>

      <section className="lab-scan-verdict">
        <div className="lab-verdict-head">
          <div>
            <VerdictPill verdict={fit.verdict} />
            <ConfidenceDot confidence={fit.confidence} />
          </div>
          <h3>{fit.verdictHeadline}</h3>
          <p>{fit.explanation}</p>
        </div>

        <div className="lab-verdict-grid">
          <StatRow label="Model" value={`${model.displayName}`} />
          <StatRow label="Base coding tier" value={<TierBadge tier={model.codingTier} />} />
          <StatRow label="With quant" value={<TierBadge tier={fit.codingUsefulnessTier} />} />
          <StatRow label="Quant" value={QUANT_LABEL[fit.quantization]} />
          <StatRow label="Required memory" value={`${fit.requiredGb} GB`} />
          <StatRow label="Headroom" value={`${fit.headroomRatio}×`} />
          <StatRow label="Expected speed" value={`${fit.expectedTps} tok/s`} />
          <StatRow label="First token" value={`${fit.expectedTtftMs} ms`} />
          <StatRow label="Max context" value={`${fit.expectedContext.toLocaleString()} tok`} />
          <StatRow label="Runtime" value={runtime} />
        </div>

        {mode === 'pro' && (
          <div className="lab-verdict-bars">
            <div>
              <span>Memory fit</span>
              <Bar
                value={Math.min(fit.headroomRatio, 3)}
                max={3}
                tone={fit.headroomRatio < 1.2 ? 'coral' : 'mint'}
              />
            </div>
            <div>
              <span>Expected speed (vs 40 tok/s target)</span>
              <Bar value={fit.expectedTps} max={40} tone="cyan" />
            </div>
          </div>
        )}

        {fit.cautions.length > 0 && (
          <div className="lab-cautions">
            <strong>Watch out</strong>
            <ul>
              {fit.cautions.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        <details className="lab-quant-table" open={mode === 'pro'}>
          <summary>All quantizations for this model on {machine.nickname}</summary>
          <table>
            <thead>
              <tr>
                <th>Quant</th>
                <th>Verdict</th>
                <th>Required</th>
                <th>Headroom</th>
                <th>Expected tok/s</th>
                <th>Coding tier</th>
              </tr>
            </thead>
            <tbody>
              {allQuantFits.map(({ q, fit: f }) => (
                <tr key={q} className={q === activeQuant ? 'lab-row-active' : ''}>
                  <td>{QUANT_LABEL[q]}</td>
                  <td>
                    <VerdictPill verdict={f.verdict} />
                  </td>
                  <td>{f.requiredGb} GB</td>
                  <td>{f.headroomRatio}×</td>
                  <td>{f.expectedTps}</td>
                  <td>
                    <TierBadge tier={f.codingUsefulnessTier} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      </section>
    </div>
  );
}
